import React, { useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/pro-regular-svg-icons/faImage";
import { faImageSlash } from "@fortawesome/pro-regular-svg-icons/faImageSlash";
import { faSpinner } from "@fortawesome/pro-regular-svg-icons/faSpinner";
import { faArrowsRotate } from "@fortawesome/pro-regular-svg-icons/faArrowsRotate";
import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";

import { useUpdateElementData } from "../DocumentView/DocumentContext";
import { useFormResultUpload, useResultFile } from "./useFormResultUpload";
import { cn } from "../lib/utils";

interface Props {
  element: DocumentElementType;
}

/**
 * A page-document image block. Purpose-built for edit-in-place: click to
 * upload/replace the image inline (via the host's file transport), storing the
 * attachment id in `data.attachmentId`. Alt text + fit live in `data` (edited
 * via the config panel). Read-only outside the editor.
 */
const PageImage: React.FC<Props> = ({ element }) => {
  const data = element.data as {
    attachmentId?: number | null;
    /** Direct image URL (Unsplash / pasted link) — no attachment. */
    imageUrl?: string | null;
    alt?: string;
    fit?: string;
  };
  const attachmentId =
    typeof data?.attachmentId === "number" ? data.attachmentId : null;
  const directUrl =
    typeof data?.imageUrl === "string" && data.imageUrl ? data.imageUrl : null;
  const alt = typeof data?.alt === "string" ? data.alt : "";
  const fit = data?.fit === "cover" ? "object-cover" : "object-contain";

  const update = useUpdateElementData(element.id);
  const editable = !!update;

  const { file, missing } = useResultFile(attachmentId);
  // A direct URL (Unsplash / link) resolves without the file transport.
  const imgUrl = directUrl ?? file?.thumbnailUrl ?? file?.url ?? null;
  // Nothing to wait for: the file is gone or withheld from this reader, or it
  // resolved without a URL to show.
  const unavailable = !imgUrl && (missing || !!file);
  const { pending, pickFiles } = useFormResultUpload();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onPick = useCallback(
    async (picked: File[]) => {
      if (!picked.length || !update) return;
      const refs = await pickFiles(picked.slice(0, 1));
      if (refs.length) {
        update((d) => {
          d.attachmentId = refs[0].fileId;
        });
      }
    },
    [pickFiles, update],
  );

  const fileInput = editable ? (
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      className="hidden"
      onChange={(e) => {
        void onPick(Array.from(e.target.files ?? []));
        e.target.value = "";
      }}
    />
  ) : null;

  const isUploading = pending.some((p) => p.state === "uploading");

  // Empty state — only interactive in the editor; nothing in view mode.
  if (attachmentId == null && !directUrl) {
    if (!editable) return null;
    return (
      <>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={cn(
            "flex w-full items-center justify-center gap-[9px] rounded-[10px] border-[1.5px] border-dashed",
            "border-[#e2e0db] p-[20px] text-[13.5px] font-medium text-[#a5a199] transition-colors",
            "hover:border-[#c9c5be] hover:bg-[#faf9f7] hover:text-[#8f8b83]",
          )}
          aria-label="Add image"
        >
          <FontAwesomeIcon
            icon={isUploading ? faSpinner : faImage}
            className={cn("h-4 w-4", isUploading && "animate-spin")}
          />
          {isUploading ? "Uploading…" : "Add an image"}
        </button>
        {fileInput}
      </>
    );
  }

  return (
    <figure className="group relative">
      {imgUrl ? (
        <img
          src={imgUrl}
          alt={alt || file?.name || "Image"}
          className={cn("max-h-[520px] w-full rounded-md", fit)}
          loading="lazy"
        />
      ) : unavailable ? (
        <div className="flex h-40 flex-col items-center justify-center gap-[8px] rounded-md bg-muted/40 text-[13px] font-medium text-[var(--forms-muted)]">
          <FontAwesomeIcon icon={faImageSlash} className="h-4 w-4" />
          Image unavailable
        </div>
      ) : (
        <div className="flex h-40 items-center justify-center rounded-md bg-muted/40 text-[var(--forms-muted)]">
          <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
        </div>
      )}
      {editable && (
        <>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className={cn(
              "absolute right-2 top-2 flex items-center gap-1 rounded-md bg-black/60 px-2 py-1",
              "text-xs text-white opacity-0 transition-opacity group-hover:opacity-100",
            )}
            aria-label="Replace image"
          >
            <FontAwesomeIcon
              icon={isUploading ? faSpinner : faArrowsRotate}
              className={cn("h-3 w-3", isUploading && "animate-spin")}
            />
            Replace
          </button>
          {fileInput}
        </>
      )}
    </figure>
  );
};

export default PageImage;
